import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

// Mobile: bottom sheet anchored above the on-screen keyboard via the --vvh
// CSS variable (set by useVisualViewport in AppLayout). Desktop: centered
// modal. Compose with ResponsiveModalHeader / Body / Footer for a sticky
// header + scrolling body + sticky footer.

const ResponsiveModal = DialogPrimitive.Root;
const ResponsiveModalTrigger = DialogPrimitive.Trigger;
const ResponsiveModalPortal = DialogPrimitive.Portal;

const ResponsiveModalOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/60 backdrop-blur-sm',
      'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
));
ResponsiveModalOverlay.displayName = 'ResponsiveModalOverlay';

const ResponsiveModalContent = React.forwardRef(
  ({ className, children, overlayClassName, ...props }, ref) => (
    <DialogPrimitive.Portal>
      <ResponsiveModalOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed inset-0 z-50 flex items-end justify-center md:items-center p-0 md:p-4',
          'max-md:bottom-auto max-md:h-[var(--vvh,100dvh)] max-md:pt-[env(safe-area-inset-top)]',
        )}
        {...props}
      >
        {/* The content fills the screen (so the sheet can sit above the keyboard),
            which makes the dimmed area part of it: a click there closes the modal. */}
        <DialogPrimitive.Close asChild>
          <div aria-hidden="true" tabIndex={-1} className="absolute inset-0 cursor-default" />
        </DialogPrimitive.Close>
        <div
          className={cn(
            'relative w-full max-w-md flex flex-col overflow-hidden shadow-2xl',
            'bg-card text-card-foreground border-border',
            'rounded-t-2xl border-t border-x md:rounded-2xl md:border',
            'max-h-[min(var(--vvh,100dvh),92dvh)] md:max-h-[90vh]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom',
            'md:data-[state=open]:zoom-in-95 md:data-[state=closed]:zoom-out-95',
            'md:data-[state=open]:slide-in-from-bottom-0 md:data-[state=closed]:slide-out-to-bottom-0',
            className,
          )}
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          {children}
        </div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  ),
);
ResponsiveModalContent.displayName = 'ResponsiveModalContent';

const ResponsiveModalHeader = ({ className, ...props }) => (
  <div
    className={cn('flex items-center gap-3 px-5 py-4 border-b border-border shrink-0', className)}
    {...props}
  />
);
ResponsiveModalHeader.displayName = 'ResponsiveModalHeader';

const ResponsiveModalTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-sm font-semibold text-foreground flex-1 min-w-0', className)}
    {...props}
  />
));
ResponsiveModalTitle.displayName = 'ResponsiveModalTitle';

const ResponsiveModalDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-xs text-muted-foreground', className)}
    {...props}
  />
));
ResponsiveModalDescription.displayName = 'ResponsiveModalDescription';

const ResponsiveModalClose = React.forwardRef(({ className, children, ...props }, ref) => {
  const { t } = useTranslation();
  return (
  <DialogPrimitive.Close
    ref={ref}
    className={cn(
      'rounded-md p-1.5 text-muted-foreground hover:bg-muted/40 hover:text-foreground transition-colors',
      className,
    )}
    {...props}
  >
    {children ?? (
      <>
        <X className="h-4 w-4" />
        <span className="sr-only">{t('common.close')}</span>
      </>
    )}
  </DialogPrimitive.Close>
  );
});
ResponsiveModalClose.displayName = 'ResponsiveModalClose';

const ResponsiveModalBody = ({ className, ...props }) => (
  <div className={cn('flex-1 min-h-0 overflow-y-auto px-5 py-4', className)} {...props} />
);
ResponsiveModalBody.displayName = 'ResponsiveModalBody';

const ResponsiveModalFooter = ({ className, ...props }) => (
  <div
    className={cn(
      // Mobile: spread Cancel (left) and the primary action (right) across the
      // full width; desktop keeps them clustered on the right.
      'flex items-center justify-end max-md:justify-between gap-2 px-5 py-3 border-t border-border shrink-0 bg-card',
      className,
    )}
    {...props}
  />
);
ResponsiveModalFooter.displayName = 'ResponsiveModalFooter';

export {
  ResponsiveModal,
  ResponsiveModalTrigger,
  ResponsiveModalPortal,
  ResponsiveModalOverlay,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
  ResponsiveModalClose,
  ResponsiveModalBody,
  ResponsiveModalFooter,
};
